import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import "./add-edit-user-modal.scss";
import ManageMemberUtils from "../../utils/manage-member-utils";

const AddEditUserModal = ({ show, onClose, user, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    phone: "",
  });
  const [error, setError] = useState("");

  // Password requirements modal state (only for add mode)
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    number: false,
    uppercase: false,
    lowercase: false,
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [mouseOverModal, setMouseOverModal] = useState(false);

  // Validate password on change (only when adding)
  useEffect(() => {
    if (!user) {
      setPasswordValidations({
        length: formData.password.length >= 8 && formData.password.length <= 20,
        number: /[0-9]/.test(formData.password),
        uppercase: /[A-Z]/.test(formData.password),
        lowercase: /[a-z]/.test(formData.password),
      });
    }
  }, [formData.password, user]);

  // Hide modal when input blur and not hovering modal (only when adding)
  useEffect(() => {
    if (!showPasswordModal && mouseOverModal && !user) {
      setShowPasswordModal(true);
    }
  }, [showPasswordModal, mouseOverModal, user]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "",
        phone: user.phone || "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "",
        phone: "",
      });
    }
    setError("");
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const { name, email, password, role } = formData;
    if (!name) return "Name is required.";
    if (!email) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return "Invalid email format.";
    if (!role) return "Role is required.";
    if (!user) {
      if (!password)
        return "Password is required when creating a new user.";
      // Password requirements validation
      const validations = {
        length: password.length >= 8 && password.length <= 20,
        number: /[0-9]/.test(password),
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
      };
      if (!Object.values(validations).every(Boolean)) {
        return "Password must be 8-20 characters, include a number, an uppercase and a lowercase letter.";
      }
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorMsg = validateForm();
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    try {
      setError("");
      if (user) {
        // Edit mode
        await ManageMemberUtils.updateMember({ ...user, ...formData });
        console.log("User updated:", formData);
      } else {
        // Add mode
        await ManageMemberUtils.addNewMember(formData);
        console.log("New user added:", formData);
      }

      onUserUpdated(); // Callback to refresh the user list in the parent component
      onClose(); // Close the modal
    } catch (error) {
      setError("An error occurred. Please try again.");
      console.error("Error:", error);
    }
  };

  return (
    <div
      className={`custom-modal-overlay ${show ? "show" : ""}`}
      role="dialog"
      aria-labelledby="newUserAddorEditModalTitle"
      aria-hidden={!show}
    >
      <div className="custom-modal-content">
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="newUserAddorEditModalTitle">
            {user ? "Edit User" : "Add New User"}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        <div className="modal-body">
          <div className="modal-image-section">
            <img
              src="assets/images/small/add-user.svg"
              alt="User Illustration"
              className="img-fluid"
            />
          </div>
          <div className="modal-form-section">
            {error && <div className="alert alert-danger">{error}</div>}
            {/* <h5 className="modal-subtitle">
              {user
                ? `Edit Details for user ${user.name}`
                : "Enter Details for New User"}
            </h5> */}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={!!user}
                />
              </div>
              {!user && (
                <div className="form-group" style={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <label>Password</label>
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setShowPasswordModal(true)}
                      onBlur={() => setTimeout(() => { if (!mouseOverModal) setShowPasswordModal(false); }, 150)}
                      required
                      autoComplete="off"
                      style={{ zIndex: 2, position: 'relative' }}
                    />
                    {/* Password requirements modal-like tooltip */}
                    {showPasswordModal && (
                      <div
                        className="password-requirements-modal"
                        style={{
                          position: 'absolute',
                          left: 'calc(100% + 30px)',
                          top: 15,
                          zIndex: 10,
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: 6,
                          padding: '14px 18px',
                          minWidth: 220,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                          fontSize: 14,
                          transition: 'opacity 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onMouseEnter={() => setMouseOverModal(true)}
                        onMouseLeave={() => { setMouseOverModal(false); setShowPasswordModal(false); }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: -16,
                            top: 20,
                            width: 0,
                            height: 0,
                            borderTop: '8px solid transparent',
                            borderBottom: '8px solid transparent',
                            borderRight: '16px solid #ccc',
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            left: -14,
                            top: 21,
                            width: 0,
                            height: 0,
                            borderTop: '7px solid transparent',
                            borderBottom: '7px solid transparent',
                            borderRight: '14px solid #fff',
                          }}
                        />
                        <ul style={{margin: 0, paddingLeft: 18, listStyle: 'disc'}}>
                          <li style={{color: passwordValidations.length ? 'green' : 'red'}}>
                            8 - 20 characters
                          </li>
                          <li style={{color: passwordValidations.number ? 'green' : 'red'}}>
                            1 number
                          </li>
                          <li style={{color: passwordValidations.uppercase ? 'green' : 'red'}}>
                            1 uppercase letter
                          </li>
                          <li style={{color: passwordValidations.lowercase ? 'green' : 'red'}}>
                            1 lowercase letter
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Role</label>
                <select
                  className="form-select"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select a role --</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div className="form-group">
                <label>Phone (Optional)</label>
                <input
                  type="tel"
                  className="form-control"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </form>
          </div>
        </div>
        <div className="modal-footer justify-content-end">
          <button
            type="button"
            className="btn"
            onClick={onClose}
          >
            <span>Close</span>
          </button>
          <button
            type="submit"
            className="bouncy-button small"
            onClick={handleSubmit}
          >
            <span>{user ? "Save Changes" : "Add User"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

AddEditUserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onUserUpdated: PropTypes.func.isRequired, // Callback to refresh user list
};

export default AddEditUserModal;
